import { useState } from 'react'
import { Loader2, Paperclip, Mail, AlertTriangle, FileText, Download } from 'lucide-react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useEmailLog } from '@/hooks/useEmailLog'
import { useSupabase } from '@/lib/supabase-context'
import { emailLogService, type EmailLogEntry } from '@/services/emailLogService'
import { formatDateTime as formatDate } from '@/lib/utils'
import { etiquetaEstadoEmail, etiquetaTipoEmail } from '@/lib/email-labels'

interface EmailDetailSheetProps {
  emailId: string | null
  organizacionId?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function formatFileSize(bytes: number): string {
  return `${(bytes / 1024).toFixed(1)} KB`
}

export function EmailDetailSheet({
  emailId,
  organizacionId,
  open,
  onOpenChange,
}: EmailDetailSheetProps) {
  const { data, isLoading, isError } = useEmailLog(emailId ?? '', organizacionId)
  const email = data as EmailLogEntry | undefined

  const { supabase } = useSupabase()
  const [descargando, setDescargando] = useState<string | null>(null)

  // ORR-170: firma una URL temporal del adjunto persistido en Storage y la abre.
  const handleDescargarAdjunto = async (path: string) => {
    if (!supabase) return
    setDescargando(path)
    try {
      const url = await emailLogService.crearUrlAdjunto(supabase, path)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      console.error('Error al abrir adjunto:', err)
      toast.error('No se pudo abrir el adjunto')
    } finally {
      setDescargando(null)
    }
  }

  const statusInfo = email ? etiquetaEstadoEmail(email.status) : null
  const tipoLabel = email ? etiquetaTipoEmail(email.tipo) : null
  const contratoUrl =
    email?.tipo === 'contrato_cliente' && email.metadata && typeof email.metadata.contrato_url === 'string'
      ? email.metadata.contrato_url
      : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-2xl flex flex-col overflow-hidden p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            Detalle del Email
          </SheetTitle>
        </SheetHeader>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && email && (
          <div className="flex flex-col flex-1 overflow-y-auto px-6 py-4 gap-4">
            {/* Badges de estado y tipo */}
            <div className="flex flex-wrap gap-2">
              {statusInfo && (
                <Badge variant={statusInfo.variant}>{statusInfo.label}</Badge>
              )}
              {tipoLabel && (
                <Badge variant="outline">{tipoLabel}</Badge>
              )}
            </div>

            {/* Metadatos del email */}
            <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <span className="text-muted-foreground font-medium">De:</span>
              <span className="break-all">{email.from}</span>

              <span className="text-muted-foreground font-medium">Para:</span>
              <span className="break-all">{(email.to ?? []).join(', ')}</span>

              <span className="text-muted-foreground font-medium">Fecha:</span>
              <span>{formatDate(email.created_at)}</span>

              <span className="text-muted-foreground font-medium">Asunto:</span>
              <span className="break-words">{email.subject}</span>
            </div>

            {/* Adjuntos */}
            {email.attachments_meta && email.attachments_meta.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-3">
                    Adjuntos ({email.attachments_meta.length})
                  </p>
                  <ul className="space-y-2">
                    {email.attachments_meta.map((att, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-2 text-sm text-muted-foreground"
                      >
                        <Paperclip className="h-4 w-4 shrink-0" />
                        <span className="flex-1 break-all">{att.filename}</span>
                        <span className="shrink-0 text-xs tabular-nums">
                          {formatFileSize(att.size)}
                        </span>
                        {att.path ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 shrink-0 px-2"
                            disabled={descargando === att.path}
                            onClick={() => handleDescargarAdjunto(att.path!)}
                          >
                            {descargando === att.path ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Download className="h-3.5 w-3.5" />
                            )}
                            <span className="ml-1">Descargar</span>
                          </Button>
                        ) : (
                          <span className="shrink-0 text-xs text-muted-foreground/60">
                            no disponible
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {/* Contrato: link a la vista pública que regenera el PDF */}
            {contratoUrl && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Contrato</p>
                  <a
                    href={contratoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium hover:bg-accent"
                  >
                    <FileText className="h-4 w-4 shrink-0" />
                    Ver contrato
                  </a>
                  <p className="text-xs text-muted-foreground mt-2">
                    Abre el contrato enviado al cliente.
                  </p>
                </div>
              </>
            )}

            {/* Contenido HTML */}
            <Separator />
            <div className="flex flex-col gap-2 flex-1 min-h-0">
              <p className="text-sm font-semibold text-foreground shrink-0">Contenido</p>
              {email.body_html ? (
                <iframe
                  srcDoc={email.body_html}
                  sandbox=""
                  referrerPolicy="no-referrer"
                  title="Contenido del email"
                  className="w-full flex-1 min-h-[300px] rounded-md border bg-white"
                />
              ) : (
                <div className="rounded-md bg-muted px-4 py-3 text-sm text-muted-foreground">
                  El contenido de este email no fue registrado. Los emails enviados a partir de ahora incluirán esta información.
                </div>
              )}
            </div>

            {/* Error (solo si existe) */}
            {email.error && (
              <>
                <Separator />
                <div className="flex gap-2 rounded-md bg-destructive/10 px-4 py-3">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
                  <p className="text-sm text-destructive break-words whitespace-pre-wrap">
                    {email.error}
                  </p>
                </div>
              </>
            )}
          </div>
        )}

        {!isLoading && isError && (
          <div className="flex flex-1 items-center justify-center px-6">
            <div className="flex gap-2 items-center">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <p className="text-sm text-destructive">Error al cargar el email. Intentá de nuevo.</p>
            </div>
          </div>
        )}

        {!isLoading && !isError && !email && emailId && (
          <div className="flex flex-1 items-center justify-center px-6">
            <p className="text-sm text-muted-foreground">No se encontró el email.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
